import { useCallback, useRef } from 'react';
import { useEvent } from '../../../core';
import { PluginLoader, PluginMetadata, PluginModuleResource, PluginType } from '../../model';

export interface PluginIndexes {
  // Plugin resources by plugin type and kind (i.e. look up what module a plugin type and kind is in)
  pluginResourcesByTypeAndKind: Map<string, PluginModuleResource>;
  // Plugin metadata by plugin type
  pluginMetadataByType: Map<PluginType, PluginMetadata[]>;
}

/**
 * Returns an async callback for getting indexes of the installed plugin data.
 */
export function usePluginIndexes(getInstalledPlugins: PluginLoader['getInstalledPlugins']) {
  // Creates indexes from the installed plugins data (does useEvent because this accesses the getInstalledPlugins prop
  // and we want a stable reference for the callback below)
  const createPluginIndexes = useEvent(async (): Promise<PluginIndexes> => {
    const installedPlugins = await getInstalledPlugins();

    // Create the two indexes from the installed plugins
    const pluginResourcesByTypeAndKind = new Map<string, PluginModuleResource>();
    const pluginMetadataByType = new Map<PluginType, PluginMetadata[]>();

    for (const resource of installedPlugins) {
      for (const pluginMetadata of resource.spec.plugins) {
        const { pluginType, kind } = pluginMetadata;

        // Index the plugin by type and kind to point at the module that contains it
        const key = getTypeAndKindKey(pluginType, kind);
        if (pluginResourcesByTypeAndKind.has(key)) {
          // eslint-disable-next-line no-console
          console.warn(`Got more than one ${pluginType} plugin for kind ${kind}`);
        }
        pluginResourcesByTypeAndKind.set(key, resource);

        // Index the metadata by plugin type
        let list = pluginMetadataByType.get(pluginType);
        if (list === undefined) {
          list = [];
          pluginMetadataByType.set(pluginType, list);
        }
        list.push(pluginMetadata);
      }
    }

    return {
      pluginResourcesByTypeAndKind,
      pluginMetadataByType,
    };
  });

  // De-dupe creating plugin indexes (i.e. requests to get installed plugins)
  const pluginIndexesCache = useRef<Promise<PluginIndexes> | undefined>(undefined);
  const getPluginIndexes = useCallback(() => {
    let request = pluginIndexesCache.current;
    if (request === undefined) {
      request = createPluginIndexes();
      pluginIndexesCache.current = request;

      // Remove failed requests from the cache so they can potentially be retried
      request.catch(() => pluginIndexesCache.current === undefined);
    }
    return request;
  }, [createPluginIndexes]);

  return getPluginIndexes;
}

/**
 * Gets a unique key for a plugin type/kind that can be used as a cache key.
 */
export function getTypeAndKindKey(pluginType: PluginType, kind: string) {
  return `${pluginType}:${kind}`;
}
